import { useMemo, useRef, useState } from "react";
import { envelopeFor, errorsOf, isLegal, serializeEnvelope, warningsOf } from "@personnel/core";
import { ATTRIBUTES, ATTRIBUTE_IDS, leviathanRules } from "@personnel/rules-leviathan";
import { rankTitle } from "@personnel/setting";
import { downloadText, slugify } from "../../lib/download.js";
import { useCaptainDraft } from "./useCaptainDraft.js";
import { AttributeRow } from "./AttributeRow.js";
import { EssenceRow } from "./EssenceRow.js";
import { FactionPicker } from "./FactionPicker.js";
import { ReputationRow } from "./ReputationRow.js";
import { QualityPicker } from "./QualityPicker.js";
import { BudgetPanel } from "./BudgetPanel.js";
import { CardPanel } from "./card/CardPanel.js";
import { fillCaptainsLog } from "./pdf/exportPdf.js";
import { importCaptainRecord } from "./importRecord.js";

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-display text-sm font-semibold tracking-[0.18em] text-leviathan-500 uppercase">
          {title}
        </h2>
        {hint && <span className="text-xs text-void-700">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

export function CaptainBuilder() {
  const { captain, update, setAttribute, toggleQuality, load, reset } = useCaptainDraft();

  const derived = useMemo(() => leviathanRules.derive(captain), [captain]);
  const validation = useMemo(() => leviathanRules.validate(captain), [captain]);
  const errors = errorsOf(validation);
  const warnings = warningsOf(validation);
  const legal = isLegal(validation);

  const fileRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importNote, setImportNote] = useState<string | null>(null);

  const handleImport = async (file: File | undefined) => {
    if (file === undefined) return;
    setImportError(null);
    setImportNote(null);
    try {
      const { captain: imported, writtenBy } = await importCaptainRecord(file);
      load(imported);
      setImportNote(
        writtenBy === null ? null : `Loaded from ruleset ${writtenBy}; missing fields took defaults.`,
      );
    } catch (cause) {
      setImportError(cause instanceof Error ? cause.message : "That record could not be read.");
    }
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleLogExport = async () => {
    setPdfBusy(true);
    setPdfError(null);
    try {
      const blob = await fillCaptainsLog(captain, derived);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${slugify(leviathanRules.describe(captain), "captain")}-log.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setPdfError(cause instanceof Error ? cause.message : "The Captain's Log could not be filled.");
    } finally {
      setPdfBusy(false);
    }
  };

  const handleExport = () => {
    downloadText(
      `${slugify(leviathanRules.describe(captain), "captain")}.captain.json`,
      serializeEnvelope(envelopeFor(leviathanRules, captain)),
    );
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_18rem] lg:items-start">
      <div className="flex min-w-0 flex-col gap-10">
        <Section title="Identity">
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs tracking-wide text-void-400 uppercase">Name</span>
              <input
                value={captain.name}
                onChange={(event) => update({ name: event.target.value })}
                placeholder="Tabitha VanDross"
                className="rounded-md border border-void-800 bg-void-900 px-3 py-2 text-void-200 placeholder:text-void-700 focus:border-leviathan-600 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs tracking-wide text-void-400 uppercase">Concept</span>
              <input
                value={captain.concept}
                onChange={(event) => update({ concept: event.target.value })}
                placeholder="Salvage runner turned reluctant commander"
                className="rounded-md border border-void-800 bg-void-900 px-3 py-2 text-void-200 placeholder:text-void-700 focus:border-leviathan-600 focus:outline-none"
              />
            </label>
          </div>
        </Section>

        <Section title="Faction" hint="Optional — only Princep requires one">
          <FactionPicker
            selected={captain.faction}
            onChange={(faction) => update({ faction })}
          />
        </Section>

        <Section
          title="Attributes"
          hint={`Balance floor ${derived.balanceFloor} · Rank exempt`}
        >
          <div className="rounded-xl border border-void-800 bg-void-900/40 px-4">
            {ATTRIBUTE_IDS.map((id) => (
              <AttributeRow
                key={id}
                attribute={ATTRIBUTES[id]}
                rating={captain.attributes[id]}
                floor={derived.balanceFloor}
                // Rank shows the effective rating, since a Quality can raise it.
                title={
                  id === "rank" ? rankTitle(captain.faction, derived.effectiveRank) : null
                }
                // Rank alone is capped, by the average of the other five.
                maximum={id === "rank" ? derived.maxRank : null}
                onChange={(rating) => setAttribute(id, rating)}
              />
            ))}
          </div>
        </Section>

        <Section
          title="Qualities"
          hint={`${captain.qualities.length} selected`}
        >
          <QualityPicker captain={captain} onToggle={toggleQuality} />
        </Section>

        <Section title="Essence" hint="Does not count toward Cost">
          <EssenceRow
            points={captain.essence}
            used={captain.essenceUsed}
            cost={derived.essenceCost}
            remaining={derived.essenceRemaining}
            onChange={(essence) => update({ essence: Math.max(0, essence) })}
            onUsedChange={(essenceUsed) => update({ essenceUsed: Math.max(0, essenceUsed) })}
          />
        </Section>

        <Section title="Reputation" hint="Earned in play — outside Cost and Experience">
          <ReputationRow
            renown={captain.renown}
            path={captain.reputationPath}
            tier={derived.reputationTier}
            reputation={derived.reputation}
            recommended={derived.recommendedRenown}
            onRenownChange={(renown) => update({ renown })}
            onPathChange={(reputationPath) => update({ reputationPath })}
          />
        </Section>
      </div>

      <div className="flex flex-col gap-4 lg:sticky lg:top-8">
        <CardPanel captain={captain} derived={derived} />

        <BudgetPanel captain={captain} derived={derived} onChange={update} />

        {(errors.length > 0 || warnings.length > 0) && (
          <ul className="flex flex-col gap-2 text-xs leading-relaxed">
            {errors.map((issue) => (
              <li
                key={issue.code + (issue.path?.join(".") ?? "")}
                className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 text-red-300"
              >
                {issue.message}
              </li>
            ))}
            {warnings.map((issue) => (
              <li
                key={issue.code + issue.message}
                className="rounded-md border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-amber-200/80"
              >
                {issue.message}
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          onClick={handleExport}
          disabled={!legal}
          className="rounded-md bg-leviathan-600 px-4 py-2.5 text-sm font-medium text-void-950 transition-colors hover:bg-leviathan-500 disabled:cursor-not-allowed disabled:bg-void-800 disabled:text-void-700"
        >
          Export record
        </button>

        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(event) => {
            void handleImport(event.target.files?.[0]);
            // Clear it so re-picking the same file fires change again.
            event.target.value = "";
          }}
        />

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="rounded-md border border-void-800 px-4 py-2.5 text-sm font-medium text-void-400 transition-colors hover:border-leviathan-600 hover:text-leviathan-500"
        >
          Import record
        </button>

        {importError !== null && (
          <p className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-300">
            {importError}
          </p>
        )}
        {importNote !== null && (
          <p className="rounded-md border border-leviathan-600/30 bg-leviathan-600/5 px-3 py-2 text-xs text-void-400">
            {importNote}
          </p>
        )}

        <button
          type="button"
          onClick={() => void handleLogExport()}
          disabled={!legal || pdfBusy || captain.faction === null}
          title={
            captain.faction === null
              ? "Choose a faction — each one has its own Captain's Log."
              : undefined
          }
          className="rounded-md border border-leviathan-600/50 px-4 py-2.5 text-sm font-medium text-leviathan-500 transition-colors hover:border-leviathan-600 hover:bg-leviathan-600/10 disabled:cursor-not-allowed disabled:border-void-800 disabled:text-void-700"
        >
          {pdfBusy ? "Filling…" : "Captain's Log (PDF)"}
        </button>

        {pdfError !== null && (
          <p className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-300">
            {pdfError}
          </p>
        )}

        <button
          type="button"
          onClick={reset}
          className="text-xs text-void-700 transition-colors hover:text-void-400"
        >
          Start over
        </button>
      </div>
    </div>
  );
}
